/* 151 §2 — THE ONE DOCUMENT MODAL.
 *
 * Step 1 (the extraction) collapsed CaseStageTimeline's and TaskDocsPane's
 * dialogs into this component, faithfully. Step 2 is the RULED face:
 *
 *   (ii)  THE LINK SECTION IS ON TOP, and the link populates the fields below.
 *   (iii) Name, file type and date are INFERRED AND READ-ONLY:
 *         - from Casefile/OneDrive: the real name, the real type (detFT), and
 *           the file's LAST-MODIFIED date — never today's date, never the
 *           record date. `lastModified` rides the picker's picked contract
 *           (OneDrivePicker.tsx; /browse has surfaced it all along). A
 *           casefile item that did not answer it keeps an EMPTY date rather
 *           than a false today.
 *         - from a pasted weblink: name = the beginning of the URL, truncated;
 *           type = "weblink"; date = the date of RECORDING (the day the link
 *           was known good) — and the date LABEL follows the link kind
 *           ("Date recorded" vs "Date of version"), or it reads wrong half
 *           the time.
 *   (iv)  The user completes only Type · Status of document · Link to Stage ·
 *         Note, in that order — behind two capability props, because the two
 *         store families differ (§1: four stores, two shapes):
 *         - `classify` (Type · Status · Note) exists only where the store
 *           holds those fields: the matter docs[] rows the stage hosts write.
 *           TaskDoc/AgendaEventDoc hold label + url and get no fields their
 *           store would silently drop.
 *         - `stageLink` (the Link to Stage select) is REQUIRED on the stage
 *           hosts (§4 removes the unstaged case) and arrives for tasks/events
 *           in step 4, optional, where choosing one creates the task/event ↔
 *           stage link.
 *         The create-at-once OneDrive arm (109 §7b's onPickCreate) is RETIRED
 *         with this: a pick that creates the row at once completes none of
 *         (iv)'s fields — it would stamp type "other" and no status sight
 *         unseen, on the only surface able to say better (§4 deletes the
 *         panes that could re-classify). A pick now FILLS the draft
 *         everywhere, and the user's own fields survive re-inference.
 *
 * The other host contracts from step 1 are unchanged: the draft is internal
 * unless lifted (TaskDocsPane's upload writes it from outside); uploadSlot is
 * 127 §1's machinery, host-owned. onSave hands the host the whole draft — a
 * host whose store cannot hold a field simply does not read it; storage
 * unification is NOT in this brief.
 */
import { useRef, useState } from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import { FS, Z } from "../../lib/tokens";
import type { TaxList, TFn } from "../../lib/taxonomyTypes";
import type { MatterKind, MatterFileConflict } from "../../api";
import { uploadMatterFile } from "../../api"; // 127 §1, machinery MOVED here (151 §3)
import MatterFileConflictDialog, { type MatterFileConflictState } from "../MatterFileConflictDialog"; // 127 §2
import { ModalLayout, SaveButton, CancelButton } from "../SharedUI";
import { openOneDrivePicker } from "../../lib/loadOnUse.mjs"; // 208 §1 — the picker loads the first time it opens
import { stageLabel } from "../StageLinkField";
import { resolveTaxonomyLabel } from "../../lib/taxonomyHelpers";
import MenuSelect from "../MenuSelect"; // 151F §2 — the app's own dropdown
import { useClipboardReadable } from "../../hooks/useClipboardReadable"; // 151G §2
import { detFT } from "../../utils";

export type DocEntryDraft = {
  name: string;
  link: string;
  /** detFT code, or "weblink" */
  fileType?: string;
  /** YYYY-MM-DD — last-modified for a casefile item, recorded-on for a
   *  weblink, "" when a casefile item did not answer (never a false today) */
  date?: string;
  source?: "casefile" | "weblink";
  /* (iv) — what the user completes. Read only by hosts whose store holds
     them (the matter docs[] rows); a lean host never sees them filled. */
  /** document_category code */
  type?: string;
  /** document_status code */
  status?: string;
  /** the stage this document sits in (stage hosts) or links to (step 4) */
  stageId?: number | null;
  note?: string;
};

/** one stage, in the read-set the Link to Stage select needs — the hosts'
 *  own stage rows (matter JSON) all satisfy it */
export type StageOption = {
  id: number;
  type?: string | null;
  startDate?: string | null;
  registerNumber?: string | number | null;
  [k: string]: unknown;
};

/* 151D §4(c) — the three source rows share ONE geometry: the buttons
   column FIRST, the white fields SECOND. A fixed button track and a 1fr
   field track, stretch-aligned, so the three buttons stand vertically
   aligned at identical widths and heights, and the three fields likewise.
   151F §1 — measured before believing the glance's mechanism: the three
   boxes were ALREADY identical (142.14×32.3 visual px each, same x), and
   the fields likewise — what read as "text bits of varying widths" was
   the ghost dress painting no background and a hairline border, so the
   centred INK was the only visible part. The fix is therefore the DRESS,
   not the grid: the buttons wear `btn btn-primary` — the app's own small
   action button, the very recipe of the `+ Add` (AddButton) that opens
   this dialog — so the equal boxes finally show. */
const SRC_ROW: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "132px 1fr",
  gap: 6,
  alignItems: "stretch",
};
const SRC_BTN: React.CSSProperties = {
  width: "100%",
  justifyContent: "center",
  padding: "3px 8px",
  fontSize: FS.s10,
};

/** the beginning of the URL, truncated — the ruled weblink name */
const NAME_CAP = 60;
const weblinkName = (link: string) =>
  link.length > NAME_CAP ? link.slice(0, NAME_CAP) + "…" : link;

export const inferWeblink = (link: string): DocEntryDraft => ({
  name: weblinkName(link.trim()),
  link,
  fileType: "weblink",
  date: new Date().toISOString().slice(0, 10),
  source: "weblink",
});

export const inferCasefile = (p: {
  name?: string | null;
  webUrl?: string | null;
  lastModified?: string | null;
}): DocEntryDraft => ({
  name: String(p.name || ""),
  link: String(p.webUrl || ""),
  fileType: detFT(String(p.name || "")),
  date: p.lastModified ? String(p.lastModified).slice(0, 10) : "",
  source: "casefile",
});

type Props = {
  t: TFn;
  /** the task pane's edit variant: edit title, Save label, the ghost Open */
  editing?: boolean;
  initial?: DocEntryDraft | null;
  onClose: () => void;
  /** link guaranteed non-empty (trimmed); the host writes through its own
   *  path and decides whether to close (a failed create keeps the dialog) */
  onSave: (d: DocEntryDraft) => void;
  /** the edit variant's Open — the host keeps its own href fork (126 §9) */
  onOpen?: (link: string) => void;
  /** the matter binding for the picker; absent = no OneDrive button. The
   *  nullable members are TaskDocsPane's own prop shape, carried verbatim. */
  oneDrive?: { caseId?: number | string | null; ccId?: number | string | null } | null;
  /** (iv) — Type · Status of document · Note, for hosts whose store holds
   *  them (the matter docs[] rows). docTypesList is document_category's
   *  tuple display shape ([code, label]); docStatusesList document_status's
   *  vlc shape ({v, l, c}) — both the hosts' existing feeds, threaded. */
  classify?: {
    docTypesList: ReadonlyArray<readonly [string, string]>;
    docStatusesList: ReadonlyArray<{ v: string; l: string; c?: string }>;
  } | null;
  /** (iv)/(v) — the Link to Stage select over the matter's FULL stage list
   *  (never a page of it). `entries` is the case_stage taxonomy, so options
   *  say the stage type's name, as the timeline row does. `required` is the
   *  stage hosts' rule (§4 removes the unstaged case). */
  stageLink?: {
    stages: ReadonlyArray<StageOption>;
    entries?: TaxList;
    required?: boolean;
  } | null;
  /** 151 §3 — 127 §1's upload-and-attach control, MOVED into the modal (it
   *  was the pane's `uploadSlot`; one implementation, two variants is what
   *  stops browse and add drifting apart). The machinery (hidden input ·
   *  uploadMatterFile · the 127 §2 conflict dialog) is all this component's.
   *  Needs a resolvable `oneDrive` binding to act.
   *  (xi)(c) — `pickFolder` is the stage variant's Browse: the OS file
   *  dialog, THEN the casefile browser for the folder it is saved into, and
   *  the label follows the behaviour ("Browse" vs 127 §1's "Add to case
   *  files" — the task variant's quick-attach keeps its ruled root filing
   *  and its own words). */
  upload?: { pickFolder?: boolean } | null;
  /** LIFTED DRAFT — the MatterPicker query rule: internal when omitted (the
   *  default; unmount is the reset), lifted when a writer OUTSIDE the dialog
   *  exists (a host reading the draft). The setter takes React's functional
   *  form too — the upload merges into the draft AFTER a round trip the
   *  dialog stays live through, and a stale-copy merge would drop what the
   *  user typed meanwhile (the pane's own semantics, carried with the move). */
  draft?: DocEntryDraft;
  setDraft?: (d: DocEntryDraft | ((p: DocEntryDraft) => DocEntryDraft)) => void;
  zIndex?: number;
};

export default function DocumentEntryModal({
  t,
  editing = false,
  initial,
  onClose,
  onSave,
  onOpen,
  oneDrive,
  classify,
  stageLink,
  upload,
  draft: liftedDraft,
  setDraft: setLiftedDraft,
  zIndex = Z.modal2,
}: Props) {
  /* 151C §7 — which of the three sources filled the link, for routing the
     value to that source's own white field. Prefilled edits resolve through
     the stored `source` (a casefile doc shows under source 1, a weblink
     under source 3); the upload's own fills mark themselves. */
  const [origin, setOrigin] = useState<"pick" | "upload" | "paste" | null>(null);
  /* Always called, never conditionally; only the VALUE is chosen. */
  const [ownDraft, setOwnDraft] = useState<DocEntryDraft>({
    name: initial?.name || "",
    link: initial?.link || "",
    fileType: initial?.fileType,
    date: initial?.date,
    source: initial?.source,
    type: initial?.type,
    status: initial?.status,
    stageId: initial?.stageId ?? null,
    note: initial?.note,
  });
  const draft = liftedDraft === undefined ? ownDraft : liftedDraft;
  const setDraft = setLiftedDraft === undefined ? setOwnDraft : setLiftedDraft;

  /* ── 151 §3 — 127 §1's upload, MOVED FROM TaskDocsPane VERBATIM ───────────
     (the pane's own header notes travel with it). A hidden <input type=file>
     driven by a button — the only way to open the OS dialog without a styled
     input; `uploading` is the busy state, a second click mid-flight would
     file the same bytes twice. THE DESTINATION IS THE MATTER'S ROOT FOLDER,
     127 §1's ruled decision for the quick-attach: the server resolves the
     root from caseRootFolder and accepts an optional subPath, so the choice
     exists on the wire the day a variant wants it. 127 §2 — A NAME COLLISION
     IS NOT AN ERROR, IT IS A QUESTION: the 409 opens the two-choice dialog
     holding the FILE as well as the name (the bytes are already in memory;
     re-asking the OS would be a second dialog for an answered question). */
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  /* the 127 §2 hold carries the DESTINATION too — a retry must file into the
     folder the user already chose, never re-ask */
  const [conflict, setConflict] = useState<
    (MatterFileConflictState & { subPath?: string | null }) | null
  >(null);
  const fileIntoMatter = async (
    file: File,
    onConflict: "fail" | "version" = "fail",
    /** undefined = ask (pickFolder) or root (plain); a string rides a retry */
    subPathIn?: string | null,
  ) => {
    if (!oneDrive || uploading) return;
    /* `MatterKind` is `"ca" | "cc"` (api.ts), NOT `"case" | "cc"` — the
       app's own two-letter section keys (125 §2's drift, still ruled). */
    const kind: MatterKind = oneDrive.ccId != null ? "cc" : "ca";
    const id = oneDrive.ccId != null ? oneDrive.ccId : oneDrive.caseId;
    if (id == null) return;
    /* (xi)(c) — the folder choice, before anything is sent. A picked FOLDER
       answers its own subPath; a picked FILE names its folder (the intent is
       plain); "" is the matter root chosen explicitly and is honoured.
       `null` means the server never answered its floor — the picker's own
       discipline: never read "I could not tell" as the root — so refuse
       rather than file the bytes somewhere unintended. Cancel = no upload. */
    let subPath: string | null = subPathIn ?? null;
    if (subPathIn === undefined && upload?.pickFolder) {
      const dest = await openOneDrivePicker({ ...oneDrive });
      if (!dest) return;
      const sp: string | null = dest.isFolder
        ? dest.subPath
        : dest.subPath == null
          ? null
          : String(dest.subPath).split("/").slice(0, -1).join("/");
      if (sp == null) {
        window.__lxToast?.(
          t("docs.browse.no_floor", {
            fallback:
              "Couldn't resolve the case-file folder — file not uploaded.",
          }),
          "error",
        );
        return;
      }
      subPath = sp;
    }
    setUploading(true);
    try {
      const out = await uploadMatterFile(kind, id, file, { onConflict, subPath });
      /* an upload IS a casefile source: the draft takes the inferred trio —
         FUNCTIONALLY, because the dialog stays live through the round trip
         and what the user typed meanwhile is theirs to keep. The date is the
         FILE'S OWN last-modified (the OS mtime the browser hands on File) —
         (iii)'s rule: never today, never the record date; the upload route
         answers no timestamp, and the moment of filing is not the document's
         date. */
      setDraft((prev) => ({
        ...prev,
        ...inferCasefile({
          name: out.name,
          webUrl: out.webUrl,
          lastModified: file.lastModified
            ? new Date(file.lastModified).toISOString()
            : null,
        }),
      }));
      setOrigin("upload"); // §7 — the value shows under source 2's field
      setConflict(null);
    } catch (e) {
      const err = e as Error & { body?: Partial<MatterFileConflict> | null };
      const body = err.body;
      if (body && body.code === "matterFiles.name_conflict") {
        setConflict({
          file,
          name: String(body.name || file.name),
          suggestion: String(body.suggestion || ""),
          subPath,
        });
        return;
      }
      /* the house shape for a dialog with no `toast` prop — the message is
         the server's, resolved through srvErrText in api.ts */
      window.__lxToast?.(
        t("matterFiles.add_button", { fallback: "Add to case files" }) +
          ": " +
          (err.message || "upload failed"),
        "error",
      );
      setConflict(null);
    } finally {
      setUploading(false);
    }
  };

  /* (iv) — the stage hosts' rule: no unstaged save. The link guard is (ii)'s. */
  const saveBlocked =
    !draft.link.trim() || (stageLink?.required === true && draft.stageId == null);
  const save = () => {
    if (saveBlocked) return;
    onSave({ ...draft, name: draft.name.trim(), link: draft.link.trim() });
  };

  /* (ii) — typing a link IS the weblink path: every keystroke re-infers the
     three fields below. A pick sets source "casefile"; typing over it makes
     the entry a weblink again, because the link now says so. The fields the
     USER completed (type · status · stage · note) are theirs, not inferred —
     re-inference never touches them, and clearing the link only clears what
     the link put there. */
  const onLinkChange = (v: string) => {
    setOrigin(v.trim() ? "paste" : null); // §7 — typing claims source 3
    if (!v.trim()) {
      setDraft({ ...draft, link: v, name: "", fileType: undefined, date: undefined, source: undefined });
      return;
    }
    setDraft({ ...draft, ...inferWeblink(v.trim()), link: v });
  };

  /* 151D §4(b) — the Paste button reads the clipboard into the weblink
     path (the (ii) re-inference, unchanged underneath); keyboard pasting
     into the field of course stays.
     151G §2 — the button is HONEST ABOUT ITS LIMITS: where the engine
     refuses button-initiated clipboard reads (WebKit; old Firefox; a
     'denied' Chromium permission) it renders unresponsive with only the
     hover text, and the toast below fires only for the one remaining
     runtime path — a Chromium prompt declined at click time. */
  const canPaste = useClipboardReadable();
  const pasteLink = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt && txt.trim()) onLinkChange(txt.trim());
    } catch {
      window.__lxToast?.(
        t("docs.add.paste_fail", { fallback: "Clipboard unavailable — paste with Ctrl+V" }),
        "error",
      );
    }
  };

  /* §7 — where the chosen path/link APPEARS: exactly one of the three white
     fields carries it, the one whose source filled it. */
  const effOrigin =
    origin ??
    (draft.source === "casefile" ? "pick" : draft.source === "weblink" ? "paste" : null);

  /* what the read-only rows SHOW — stored values first, else derived for
     display (an edit of a lean row carries only name+link) */
  const shownType =
    draft.fileType || (draft.link.trim() ? "weblink" : "") || "";
  const dateLabel =
    (draft.source || "weblink") === "casefile"
      ? t("docs.field.date_version", { fallback: "Date of version" })
      : t("docs.field.date_recorded", { fallback: "Date recorded" });
  const RO: React.CSSProperties = {
    fontSize: FS.s11,
    color: "var(--ts)",
    padding: "4px 2px",
    minHeight: 18,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <ModalLayout
      title={
        editing
          ? t("task.docs.edit_title", { fallback: "Edit document link" })
          : t("docs.add_title", { fallback: "Add document link" })
      }
      onClose={onClose}
      onSave={save}
      saveDisabled={saveBlocked}
      saveLabel={
        editing
          ? t("common.save", { fallback: "Save" })
          : t("common.add", { fallback: "Add" })
      }
      maxWidth={400}
      zIndex={zIndex}
      escToClose
      /* 109 §1b — edit mode's footer grows a small ghost Open beside Cancel,
         so a link can still be checked from inside the dialog. */
      footer={
        editing ? (
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginRight: "auto", padding: "4px 10px", fontSize: FS.s11 }}
              onClick={() => onOpen && onOpen(draft.link.trim() || (initial?.link ?? ""))}
            >
              {t("common.open", { fallback: "Open" })}
            </button>
            <CancelButton onClick={onClose} />
            <SaveButton
              label={t("common.save", { fallback: "Save" })}
              onClick={save}
              disabled={saveBlocked}
            />
          </div>
        ) : undefined
      }
    >
      {/* ── 151C §7 — THE LINK SECTION SHOWS ITS THREE SOURCES, stacked ONE
          UNDER ANOTHER in the ruled order, each with its own white field
          where the chosen path/link appears (exactly one carries it — the
          source that filled it; typing in source 3 claims the link back and
          empties the other two, the (ii) re-inference unchanged underneath).
          Sources 1 and 2 need the matter binding (oneDrive) and render only
          with it; source 2 additionally needs the host's upload arm — a host
          without either simply shows fewer sources, never a dead control. */}
      {oneDrive ? (
        <div className="field">
          <label>
            {t("docs.src.link_casefile", {
              fallback: "Link existing Case file document",
            })}
          </label>
          {/* 151D §4(c) — button FIRST, field second (SRC_ROW). */}
          <div style={SRC_ROW}>
            <button
              type="button"
              className="btn btn-primary"
              style={SRC_BTN}
              onClick={async () => {
                const picked = await openOneDrivePicker({ ...oneDrive });
                if (picked && picked.webUrl) {
                  /* merge, never replace: the pick owns the inferred trio,
                     the user owns (iv)'s fields */
                  setDraft({ ...draft, ...inferCasefile(picked) });
                  setOrigin("pick");
                }
              }}
            >
              {/* 151D §4(a) — the ruled words: "File… / Dossier… /
                  Dossier…". A new key: the translations seed is
                  insert-only, so a changed string needs one
                  (docs.add.case_file stays seeded, now unmounted). */}
              {t("docs.add.file_btn", { fallback: "File…" })}
            </button>
            <input
              className="field-input"
              readOnly
              tabIndex={-1}
              title={effOrigin === "pick" ? draft.link : undefined}
              value={effOrigin === "pick" ? draft.link : ""}
              style={{ minWidth: 0 }}
            />
          </div>
        </div>
      ) : null}
      {oneDrive && upload ? (
        <div className="field">
          <label>
            {t("docs.src.upload_then_link", {
              fallback: "Add document to case file then link",
            })}
          </label>
          <div style={SRC_ROW}>
            <button
              type="button"
              className="btn btn-primary"
              style={SRC_BTN}
              disabled={uploading}
              onClick={() => uploadInputRef.current?.click()}
            >
              {/* 151F §1(b) — the modal's three buttons wear the RULED words,
                  so the quick-attach variant's face joins the set ("Browse…"
                  here too) while its BEHAVIOUR keeps 127 §1's root filing
                  (fileIntoMatter's pickFolder fork, untouched). The busy face
                  drops the ⬆ glyph — the icon-overflowing-the-frame look is
                  ruled out with the patchwork — so it needs its own arrowless
                  key (the seed is insert-only; matterFiles.uploading keeps
                  its words for any future mount). */}
              {uploading
                ? t("docs.add.filing", { fallback: "Filing…" })
                : t("docs.add.browse_btn", { fallback: "Browse…" })}
            </button>
            <input
              className="field-input"
              readOnly
              tabIndex={-1}
              title={effOrigin === "upload" ? draft.link : undefined}
              value={effOrigin === "upload" ? draft.link : ""}
              style={{ minWidth: 0 }}
            />
            {/* 151 §3 — the upload control (fileIntoMatter above). Cleared on
                change so choosing the SAME file twice still fires; display:
                none, so no grid track sees it. */}
            <input
              ref={uploadInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                e.target.value = "";
                if (f) void fileIntoMatter(f);
              }}
            />
          </div>
        </div>
      ) : null}
      <div className="field">
        <label>
          {t("docs.src.paste_weblink", { fallback: "Paste weblink" })}
        </label>
        <div style={SRC_ROW}>
          {/* 151D §4(b) — "Paste / Coller / Plakken".
              151G §2 — where the engine refuses button-initiated reads the
              button renders UNRESPONSIVE with only the hover text [RULED].
              Deliberately NOT the disabled ATTRIBUTE: WebKit suppresses
              hover on disabled controls, and WebKit is precisely where this
              face shows — aria-disabled + no handler + dimmed keeps the
              title working there. No onClick also means Enter/Space do
              nothing: no click action, no toast. */}
          <button
            type="button"
            className="btn btn-primary"
            style={
              canPaste
                ? SRC_BTN
                : { ...SRC_BTN, opacity: 0.4, cursor: "default" }
            }
            aria-disabled={!canPaste}
            title={
              canPaste
                ? undefined
                : t("docs.add.paste_unavailable", {
                    fallback: "Copy a web page address and paste here",
                  })
            }
            onClick={canPaste ? () => void pasteLink() : undefined}
          >
            {t("docs.add.paste_btn", { fallback: "Paste" })}
          </button>
          <input
            {...NO_AUTOFILL}
            className={noAutofillClass("field-input")}
            autoFocus
            placeholder={t("docs.url_ph_web", { fallback: "Paste web link" })}
            value={effOrigin === "paste" ? draft.link : ""}
            style={{ minWidth: 0 }}
            onChange={(e) => onLinkChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
          />
        </div>
      </div>
      {/* (iii) — the inferred trio, READ-ONLY. The date label follows the
          link kind; a casefile item that answered no last-modified shows an
          empty date rather than a false today. */}
      <div className="field">
        <label>{t("docs.field.name", { fallback: "Name" })}</label>
        <div style={RO} title={draft.name}>
          {draft.name || "—"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("docs.field.filetype", { fallback: "File type" })}</label>
          <div style={RO}>{shownType || "—"}</div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{dateLabel}</label>
          <div style={RO}>{draft.date || "—"}</div>
        </div>
      </div>
      {/* (iv) — what the user completes, in the ruled order: Type · Status of
          document · Link to Stage · Note. 151F §2 — Type and Status open the
          APP'S OWN menu (MenuSelect over the 151D popover idiom) instead of
          the OS-painted native list; divider codes still render as lines,
          never values (110 §3a / 108 §6b), and the ""-option stays a real,
          pickable state with the same words. */}
      {classify && (
        <div style={{ display: "flex", gap: 10 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>{t("common.type", { fallback: "Type" })}</label>
            <MenuSelect
              className="field-input"
              zIndex={zIndex + 1}
              value={draft.type || ""}
              onChange={(v) => setDraft({ ...draft, type: v })}
              options={[["", "—"], ...classify.docTypesList]}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>
              {t("docs.field.status", { fallback: "Status of document" })}
            </label>
            <MenuSelect
              className="field-input"
              zIndex={zIndex + 1}
              value={draft.status || ""}
              onChange={(v) => setDraft({ ...draft, status: v })}
              options={[
                ["", t("case.doc.no_status", { fallback: "— no status —" })],
                ...classify.docStatusesList.map(
                  (ds) => [ds.v, ds.l] as readonly [string, string],
                ),
              ]}
            />
          </div>
        </div>
      )}
      {stageLink && (
        <div className="field">
          <label>
            {t("docs.field.stage_link", { fallback: "Link to Stage" })}
            {stageLink.required ? " *" : ""}
          </label>
          {/* 151C §6 (a) — the control is a PURPLE BUTTON opening the list,
              dressed in the links system's own token (--hll, 111 §5d — never
              a literal), and it stays purple once chosen. Empty, its closed
              face reads the ACTION ("Link to Stage"); that same first row in
              the open list is the optional variant's clear.
              154B §2 — it was written as a native select because clicking one
              IS opening the list; now the list it opens is the app's, and the
              sentence about a button describes the DOM as well as the eye. */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <MenuSelect
              className="field-input"
              style={{
                color: "var(--hll)",
                fontWeight: 600,
                background: "color-mix(in srgb, var(--hll) 8%, transparent)",
                border: "1px solid color-mix(in srgb, var(--hll) 35%, transparent)",
                cursor: "pointer",
                flex: 1,
                minWidth: 0,
              }}
              /* clears the host dialog, exactly as the Type and Status menus
                 above it do (:618, :630) */
              zIndex={zIndex + 1}
              value={draft.stageId != null ? String(draft.stageId) : ""}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  stageId: v ? Number(v) : null,
                })
              }
              options={[
                /* the required variant offers no unstaged line (§4 removes
                   that case) */
                ...(!stageLink.required
                  ? [
                      [
                        "",
                        t("docs.field.stage_link", { fallback: "Link to Stage" }),
                      ] as const,
                    ]
                  : []),
                ...stageLink.stages.map(
                  (st) =>
                    [
                      String(st.id),
                      stageLabel(
                        {
                          ...st,
                          type:
                            (st.type &&
                              resolveTaxonomyLabel(
                                st.type,
                                stageLink.entries || [],
                              )) ||
                            st.type,
                        },
                        t,
                      ),
                    ] as const,
                ),
              ]}
            />
            {/* 151C §6 (b) — editing a document that carries a stage link
                shows a purple Unlink beside it. It clears the DRAFT; the
                save reconciles (deletes the purple link), and a Cancel
                discards it like every pending field. Never on the required
                variant — a stage host's doc cannot be unstaged. */}
            {editing && !stageLink.required && draft.stageId != null && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10,
                  color: "var(--hll)",
                  border: "1px solid color-mix(in srgb, var(--hll) 35%, transparent)",
                  flexShrink: 0,
                }}
                onClick={() => setDraft({ ...draft, stageId: null })}
              >
                {t("docs.stage_unlink", { fallback: "Unlink" })}
              </button>
            )}
          </div>
        </div>
      )}
      {classify && (
        <div className="field">
          <label>{t("field.note", { fallback: "Note" })}</label>
          <input
            {...NO_AUTOFILL}
            className={noAutofillClass("field-input")}
            value={draft.note || ""}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          />
        </div>
      )}
      {/* 127 §2 — NEVER OVERWRITE. CANCEL, OR A NUMBERED VERSION. Moved with
          the machinery; MatterFileConflictDialog.tsx says everything else.
          Its own zIndex 400 stacks it over this dialog's 300. */}
      {conflict && (
        <MatterFileConflictDialog
          conflict={conflict}
          busy={uploading}
          onVersion={(c) => {
            const sp = conflict.subPath;
            setConflict(null);
            void fileIntoMatter(c.file, "version", sp ?? null);
          }}
          onCancel={() => setConflict(null)}
        />
      )}
    </ModalLayout>
  );
}
